// ── useHonestQuery: wrap react-query → {posture, data, gate, reference, retry} ──
// Wave 0 foundation, blueprint §4. Pages never hand-roll state branching: they
// read `posture`, pass it (plus gate/reference) into <HonestState>, and render
// real content only when `posture === null` (ready-with-data).
//
// Wraps the EXISTING react-query setup. The retry policy is derived from the
// honest posture via NON_RETRYABLE_POSTURES so a stable state (gate-closed /
// permission-denied / not-configured / …) is never retried, while a transient
// `unavailable`/`error` is. The PRESERVED ApiClientError + the single CO_*
// posture map are the only error inputs — we did not replace the transport.

import { useQuery } from "@tanstack/react-query";
import type { QueryKey, UseQueryOptions } from "@tanstack/react-query";
import { ApiClientError } from "../../services/api-client";
import {
  honestGateFromError,
  postureForCoachingError,
  referenceFromError,
} from "../../services/coaching-error-posture";
import type { HonestGate } from "../../services/coaching-error-posture";
import { useWalkReadinessHold } from "../../lib/walk-readiness";
import { NON_RETRYABLE_POSTURES } from "./honest-posture";

import type { HonestPosture } from "./honest-posture";
export interface HonestQueryResult<T> {
  /** null while data is present and the surface should render real content. */
  posture: HonestPosture | null;
  data: T | undefined;
  gate: HonestGate | null;
  reference: string | null;
  isEmpty: boolean;
  retry: () => void;
  error: unknown;
}

export interface HonestQueryOptions<T>
  extends Omit<UseQueryOptions<T, unknown, T, QueryKey>, "queryKey" | "queryFn"> {
  /** Distinguish resource download/upload gates from real domain outages. */
  errorPath?: string;
  /** Treat a non-empty result as empty by a custom predicate (e.g. {items:[]}). */
  isEmpty?: (data: T) => boolean;
  /** Optional posture for disabled/idle dependent reads. Defaults to ready/null for backward compatibility. */
  disabledPosture?: HonestPosture | null;
}

/** Default emptiness: a bare array, or an object whose `items` array is empty. */
function defaultIsEmpty(data: unknown): boolean {
  if (data == null) return true;
  if (Array.isArray(data)) return data.length === 0;
  if (typeof data === "object" && "items" in data) {
    const items = (data as { items?: unknown }).items;
    return Array.isArray(items) && items.length === 0;
  }
  return false;
}

/**
 * react-query retry decision keyed on the honest posture: never retry a stable
 * (non-retryable) posture. This keeps a closed gate / denial from spinning.
 */
function shouldRetry(failureCount: number, error: unknown, errorPath?: string): boolean {
  if (failureCount >= 1) return false; // conservative: at most one retry
  const posture = postureForCoachingError(error, errorPath);
  return !NON_RETRYABLE_POSTURES.has(posture);
}

export function useHonestQuery<T>(
  queryKey: QueryKey,
  queryFn: () => Promise<T>,
  options: HonestQueryOptions<T> = {},
): HonestQueryResult<T> {
  const { errorPath, isEmpty: isEmptyOption, disabledPosture = null, retry: retryOption, ...queryOptions } = options;
  const isEmptyFn = isEmptyOption ?? defaultIsEmpty;

  const query = useQuery<T, unknown, T, QueryKey>({
    queryKey,
    queryFn,
    // Posture-aware retry by default (never retry a stable/non-retryable posture);
    // a caller may override `retry` explicitly (e.g. tests, or no-retry queries).
    retry: retryOption ?? ((failureCount, error) => shouldRetry(failureCount, error, errorPath)),
    ...queryOptions,
  });

  useWalkReadinessHold(query.isLoading || (query.isPending && query.isFetching));
  let posture: HonestPosture | null = null;
  let gate: HonestGate | null = null;
  let reference: string | null = null;

  const enabled = query.fetchStatus !== "idle" || query.isLoading;
  const idlePending = query.isPending && !enabled;

  if (query.isError) {
    posture = postureForCoachingError(query.error, errorPath);
    gate = honestGateFromError(query.error);
    reference = referenceFromError(query.error);
  } else if (idlePending && disabledPosture !== null) {
    posture = disabledPosture;
  } else if (query.isPending && enabled) {
    posture = "loading";
  } else if (query.isSuccess && isEmptyFn(query.data)) {
    posture = "empty";
  }

  const isEmpty = (query.isSuccess && isEmptyFn(query.data)) || (idlePending && disabledPosture === "empty");

  return {
    posture,
    data: query.data,
    gate,
    reference,
    isEmpty,
    retry: () => void query.refetch(),
    error: query.error,
  };
}

/** Re-export so callers have one import site for the posture vocabulary. */
export { ApiClientError };
